import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutGrid, ChevronDown } from 'lucide-react';
import { AuthContext } from '@/App';
import { authorizedWorkspaces } from '@/lib/workspaces';
import { APP_ICONS } from '@/lib/appIcons';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// Standard compact App Hub control used across every TFS application header.
// Four-square (application-grid) icon in TFS green. Users authorized for more
// than one experience get a "Switch app" dropdown, each row carrying the exact
// approved module icon (shared APP_ICONS mapping) so every jump shows the right
// badge. Users with a single workspace keep the plain button to the welcome
// screen. The current login session is preserved in every case.
const TFS_GREEN = '#5FA718';
const labelOf = (w) => w.label || w.title;

export const AppHubButton = ({ className = '', variant = 'dark' }) => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const base = variant === 'dark'
    ? 'border-white/15 bg-white/5 text-white hover:bg-white/10'
    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50';
  const btn = `inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${base} ${className}`;

  // Field Portal is consolidated inside TEAM, so it is not offered as its own
  // switch-app target here (field-only users land on /field directly).
  const workspaces = authorizedWorkspaces(user).filter((w) => w.key !== 'field');
  const go = (w) => {
    if (!w) return;
    if (w.external) window.open(w.external, '_blank', 'noopener,noreferrer');
    else navigate(w.route);
  };

  // Single (or no) workspace: no switcher — plain App Hub button.
  if (workspaces.length <= 1) {
    return (
      <button
        type="button"
        onClick={() => navigate('/select-portal')}
        data-testid="app-hub-button"
        aria-label="App Hub"
        title="App Hub"
        className={btn}
      >
        <LayoutGrid size={18} strokeWidth={2.5} style={{ color: TFS_GREEN }} />
        <span className="hidden sm:inline">App Hub</span>
      </button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" data-testid="app-hub-button" aria-label="Switch app" title="Switch app" className={btn}>
          <LayoutGrid size={18} strokeWidth={2.5} style={{ color: TFS_GREEN }} />
          <span className="hidden sm:inline">App Hub</span>
          <ChevronDown size={14} className="opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60" data-testid="app-switcher-menu">
        <DropdownMenuLabel>Switch app</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {workspaces.map((w) => (
          <DropdownMenuItem key={w.key} data-testid={`switch-app-${w.key}`} onClick={() => go(w)} className="gap-2.5">
            <img src={APP_ICONS[w.key] || w.img} alt="" className="h-6 w-6 shrink-0 object-contain" />
            <span className="truncate">{labelOf(w)}</span>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="switch-app-all" onClick={() => navigate('/select-portal')} className="gap-2.5">
          <LayoutGrid size={18} style={{ color: TFS_GREEN }} />
          <span>View all apps</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AppHubButton;
